import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, FileText, Calendar, DollarSign, Clock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { formatShortDate } from '../../utils/dateFormatter';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight, read-only view of the proposal a vendor submitted on an
 * inquiry. Backed by the existing endpoint:
 *   GET /api/inquiries/{inquiry_id}/proposal
 * Vendors are authorized server-side. The dialog never mutates state — it
 * exists purely so vendors can re-read what they sent and reconcile the
 * payment breakdown at a glance.
 */
const ProposalViewDialog = ({ inquiry, booking, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !inquiry?.id) return;
    let cancelled = false;
    const fetchProposal = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/inquiries/${inquiry.id}/proposal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setProposal(res.data || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'Could not load proposal');
          toast.error('Could not load proposal');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProposal();
    return () => { cancelled = true; };
  }, [open, inquiry?.id]);

  const fmt = (n) => n != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)
    : '—';

  const statusBadge = (() => {
    if (!proposal) return null;
    const map = {
      pending: { label: 'Awaiting Response', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
      sent: { label: 'Awaiting Response', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
      accepted: { label: 'Accepted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Declined', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
      expired: { label: 'Expired', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
      superseded: { label: 'Superseded', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    };
    return map[proposal.status] || { label: proposal.status, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="proposal-view-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                Proposal
              </DialogTitle>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{inquiry?.customer_name || 'Customer'} · {inquiry?.event_type || 'Event'}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 -mt-1"
              data-testid="proposal-view-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span className="text-sm">Loading proposal…</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && !proposal && (
            <p className="text-sm text-gray-500 py-6 text-center">No proposal found for this request yet.</p>
          )}

          {!loading && !error && proposal && (
            <div className="space-y-4" data-testid="proposal-view-body">
              {/* Status row */}
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Status</span>
                {statusBadge && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusBadge.cls}`} data-testid="proposal-view-status">
                    {statusBadge.label}
                  </span>
                )}
              </div>

              {/* Agreed price */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-2 text-emerald-800">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium">Agreed Price</span>
                </div>
                <span className="text-base font-bold text-emerald-900" data-testid="proposal-view-price">
                  {fmt(proposal.agreed_price)}
                </span>
              </div>

              {/* Payment breakdown (derived from booking if available) */}
              {booking?.deposit_amount != null && (
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-100 text-xs space-y-1">
                  <p className="font-medium text-gray-700">Payment Breakdown</p>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Deposit</span>
                    <span className={booking.deposit_paid ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}>
                      {fmt(booking.deposit_amount)} {booking.deposit_paid ? '· paid' : '· pending'}
                    </span>
                  </div>
                  {booking.final_amount != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Final Payment</span>
                      <span className={booking.final_paid ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}>
                        {fmt(booking.final_amount)} {booking.final_paid ? '· paid' : '· pending'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Scope */}
              {proposal.scope_description && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Scope</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="proposal-view-scope">{proposal.scope_description}</p>
                </div>
              )}

              {/* Timeline */}
              {proposal.estimated_timeline && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Timeline</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{proposal.estimated_timeline}</p>
                </div>
              )}

              {/* Sent / expires meta */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                {proposal.created_at && (
                  <div className="flex items-start gap-2 text-xs text-gray-500">
                    <Calendar className="h-3.5 w-3.5 mt-0.5 text-gray-400" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Sent</p>
                      <p>{formatShortDate(proposal.created_at)}</p>
                    </div>
                  </div>
                )}
                {proposal.expires_at && proposal.status !== 'accepted' && (
                  <div className="flex items-start gap-2 text-xs text-gray-500">
                    <Clock className="h-3.5 w-3.5 mt-0.5 text-gray-400" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Expires</p>
                      <p>{formatShortDate(proposal.expires_at)}</p>
                    </div>
                  </div>
                )}
                {proposal.accepted_at && (
                  <div className="flex items-start gap-2 text-xs text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Accepted</p>
                      <p>{formatShortDate(proposal.accepted_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="h-9"
            data-testid="proposal-view-done"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalViewDialog;
